import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {msg} from '@lit/localize';

import {connect} from 'pwa-helpers';
import store from '../../store';
import {
	selectMaxUsableBatteryCapacity,
	selectSolarBatterySize,
	selectSolarDimmProfileData, selectSolarLuminarCountValue, selectSolarPVMaxInput,
	selectSolarPVPlotData1,
	selectSolarPVPlotData2,
	selectSolarPVPlotData3,
	selectSolarPVPlotData4, selectSolarVersion
} from '../solarSelectors';
import radio from '../../../icons/radio';
import radioChecked from '../../../icons/radio-checked';
import {setSolarGenerators} from '../solarSlice';
import {staggeredReveal} from '../../css/styleSets';
import {decision} from '../../slices/decisionsSlice';
import {selectHeight} from '../../selectors';

@customElement('solar-assembly')
export class solarAssembly extends connect(store)(LitElement) {

	static styles = [
		staggeredReveal,
		css`
			:host {
				overflow: hidden;
				width: 320px;
				flex-shrink: 0;
			}
			@media (max-width: 1366px) {
				:host {
					width: 280px;
				}
			}
			.solarAssembly {
				font: var(--font-light);
				padding: var(--padding);
				background-color: #191919;
				color: var(--color-white);
				transition: background-color 0.4s ease-out, color 0.4s ease-out;
				cursor: pointer;
			}

			.solarAssembly.selected {
				background: var(--color-widget-background-selected-dark);
			}

			.solarAssembly:hover {
				background: var(--color-widget-background-hover);
			}

			.solarAssembly.selected:hover {
				background: var(--color-widget-background-selected-dark);
			}

			.disabled {
				pointer-events: none;
			}

			dl {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
			}

			dt {
				order: 1;
				margin: 0;
			}

			dd {
				order: 2;
				margin: 0;
			}

			hr {
				border-top: 1px solid var(--color-grey4);
				border-bottom: none;
				color: var(--color-grey4)
			}

			.title {
				margin: 0;
				font: var(--solar-assembly-title);
				color: var(--color-white);
				display: flex;
				flex-direction: row;
				justify-content: space-between;
			}

			.subtitle {
				margin: 0 0 1rem 0;
				font: var(--font-widget-title);
			}

			.dark {
				color: var(--color-grey4);
			}

			.icon {
				display: inline-block;
			}

			.icon svg {
				display: block;
			}

			.scrollcontainer {
				overflow-y: auto;
			}
			.loading {
				min-height: 100px;
				animation: flash 1s infinite;
			}
			// flashing animation
			@keyframes flash {
				0% {
					opacity: 0.5;
				}
				50% {
					opacity: 1;
				}
				100% {
					opacity: 0.5;
				}
			}
        `];

	@property({type: String})
	title = 'Solarmodul';

	@property({type: Number})
	generators = 1;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.solar = state.solar.data;
		this.dimmProfile = selectSolarDimmProfileData(this.state);
		this.solarGenerators = this.solar.project.generators;
		this.luminarCount = this.solar.project.luminarCount;
		this.orientation = this.solar.project.orientation;

		switch (this.generators) {
			case 1:
				this.plotData = selectSolarPVPlotData1(state);
				break;
			case 2:
				this.plotData = selectSolarPVPlotData2(state);
				break;
			case 3:
				this.plotData = selectSolarPVPlotData3(state);
				break;
			case 4:
				this.plotData = selectSolarPVPlotData4(state);
				break;
		}
		this.translations = state.productData.translations;
		this.inputData = this.getInputData();
		this.outputData = this.getOutputData();
		this.isBasic = selectSolarVersion(state) === 'AetoBasic';
	}

	getInputData() {
		if (this.plotData) {
			// return minimal and maximal values from y
			const min = Math.floor(Math.min(...this.plotData[0].y));
			const max = Math.ceil(Math.max(...this.plotData[0].y));
			return {min: min, max: max};
		} else {
			return {min: 0, max: 0};
		}
	}

	getOutputData() {
		return {
			min: Math.min(
				selectSolarPVMaxInput(this.state)[this.generators-1].winter,
				selectMaxUsableBatteryCapacity(this.state)
			),
			max: Math.min(
				selectSolarPVMaxInput(this.state)[this.generators-1].summer,
				selectMaxUsableBatteryCapacity(this.state)
			)
		};
	}

	async onClick() {
		store.dispatch(setSolarGenerators({ id: this.generators }));
		const featureValueId = this.generators + 'm' + selectSolarLuminarCountValue(this.state) + 'L';

		/*
		await store.dispatch(decision(
			{
				variableId: 'LU_SolarUnit',
				featureValueId: featureValueId,
				state: 'Included'
			}
		)).unwrap();
		*/
		await store.dispatch(decision(
			{
				variableId: 'LU_SolarUnitHeight',
				featureValueId: this.generators,
				state: 'Included'
			}
		)).unwrap();
	}

	isSelected() {
		return this.generators === this.solarGenerators;
	}
	isDisabeled() {
		return this.generators === 1 && this.luminarCount > 2;
	}
	pvDataComplete() {
		return this.orientation?.front.pvgisDaily !== null &&
			this.orientation?.back.pvgisDaily !== null &&
			this.orientation?.left.pvgisDaily !== null &&
			this.orientation?.right.pvgisDaily !== null;
	}

	render() {
		if (!this.pvDataComplete()) {
			return html`
				<div class="solarAssembly ${classMap({
				selected: this.isSelected(),
				disabled: this.isDisabeled()
			})}" @click=${this.onClick}>
					<h3 class="title">
					<span>${this.title}</span>
					${when(this.isSelected(),
						() => html`
									<div class="icon">${radioChecked}</div>`,
						() => html`
									<div class="icon">${radio}</div>`,)
					}
					</h3>
					<h4 class="subtitle dark">${this.generators} ${this.generators === 1 ? msg('metre') : msg('metres')}</h4>
					<hr>
					<div class="loading">
						${msg('Calculating photovoltaic data. Please wait...')}
					</div>
				</div>
			`;
		}
		return html`
			<div class="solarAssembly ${classMap({
				selected: this.isSelected(),
				disabled: this.isDisabeled()
			})}" @click=${this.onClick}>
				<h3 class="title">
					<span>${this.title}</span>
					${when(this.isSelected(),
						() => html`
							<div class="icon">${radioChecked}</div>`,
						() => html`
							<div class="icon">${radio}</div>`,)
					}
				</h3>
				<h4 class="subtitle dark">${this.generators} ${msg('metres')}</h4>
				<hr>
				<div class="scrollcontainer">
					<div class="subtitle">${msg('24h Dimming profile')}</div>
					${when(this.isBasic, () => html`
						<solar-dimm-profile season="winter" title="${msg('Full Year Profile (max)')}" generators=${this.generators}></solar-dimm-profile>
					`, () => html`
						<solar-dimm-profile season="winter" generators=${this.generators}></solar-dimm-profile>
						<solar-dimm-profile season="spring" generators=${this.generators}></solar-dimm-profile>
						<solar-dimm-profile season="summer" generators=${this.generators}></solar-dimm-profile>
					`)}
					<hr>
					<div class="subtitle">${msg('Energy yield')}</div>
					<solar-pv-graph generators=${this.generators}></solar-pv-graph>
					<dl>
						<dt>${msg('Battery capacity')}</dt>
						<dd><trans-label slot="value" key="LU_Battery" context="FV.S"></trans-label> </dd>
					</dl>
					<dl>
						<dt>${msg('Energy yield')}</dt>
						<dd>${this.inputData.min} - ${this.inputData.max} Wh</dd>
					</dl>
					<dl>
						<dt>${msg('Available light point heights')}</dt>
						<dd>4 - ${Math.min(7, 4 + this.generators)} m</dd>
					</dl>
				</div>
			</div>
		`;

	}
}
